import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

/**
 * Holds the current page's header overrides for the persistent {@link AppShell}
 * header. The title is a plain string kept in context; right-side actions are
 * rendered through a portal into a slot the header publishes, rather than stored
 * in state. Storing JSX actions in state and setting them from an effect would
 * loop — the provider wraps the routed pages, so each set re-renders the page,
 * which regenerates the inline actions element and re-fires the effect.
 */
/** A single breadcrumb between Home and the current page's leaf title. */
export interface PageCrumb {
  label: string;
  href?: string;
}

interface PageChromeStore {
  title?: string;
  setTitle: (title?: string) => void;
  parent?: PageCrumb;
  setParent: (parent?: PageCrumb) => void;
  actionsSlot: HTMLElement | null;
  setActionsSlot: (element: HTMLElement | null) => void;
}

const PageChromeContext = createContext<PageChromeStore | null>(null);

/** Provides page-chrome state. Rendered once by {@link AppShell}. */
export function PageChromeProvider({ children }: { children: ReactNode }) {
  const [title, setTitleState] = useState<string>();
  const [parent, setParentState] = useState<PageCrumb>();
  const [actionsSlot, setActionsSlot] = useState<HTMLElement | null>(null);

  const setTitle = useCallback((next?: string) => {
    setTitleState((prev) => (prev === next ? prev : next));
  }, []);

  const setParent = useCallback((next?: PageCrumb) => {
    setParentState((prev) => {
      if (prev === next) return prev;
      if (prev && next && prev.label === next.label && prev.href === next.href) return prev;
      return next;
    });
  }, []);

  const store = useMemo<PageChromeStore>(
    () => ({ title, setTitle, parent, setParent, actionsSlot, setActionsSlot }),
    [title, setTitle, parent, setParent, actionsSlot],
  );

  return <PageChromeContext.Provider value={store}>{children}</PageChromeContext.Provider>;
}

function useStore(): PageChromeStore {
  const store = useContext(PageChromeContext);
  if (!store) throw new Error("PageChrome hooks must be used within a PageChromeProvider");
  return store;
}

/**
 * Sets the current page's header title, clearing it when the calling component
 * unmounts so a previous page's title never lingers. The equality guard in
 * `setTitle` keeps a re-rendering page from looping.
 */
export function usePageChrome({ title, parent }: { title?: string; parent?: PageCrumb }): void {
  const { setTitle, setParent } = useStore();
  const parentLabel = parent?.label;
  const parentHref = parent?.href;
  useEffect(() => {
    setTitle(title);
    setParent(parentLabel ? { label: parentLabel, href: parentHref } : undefined);
    return () => {
      setTitle(undefined);
      setParent(undefined);
    };
  }, [title, parentLabel, parentHref, setTitle, setParent]);
}

/** Read side used by the header to display the current title. */
export function usePageChromeTitle(): string | undefined {
  return useStore().title;
}

/** Read side used by the header to render the optional parent crumb. */
export function usePageChromeParent(): PageCrumb | undefined {
  return useStore().parent;
}

/** Ref callback the header uses to publish its actions slot element. */
export function useHeaderActionsSlot(): (element: HTMLElement | null) => void {
  return useStore().setActionsSlot;
}

/**
 * Renders its children into the header's right-side actions slot. A page mounts
 * this to place page-level actions in the persistent header without passing
 * anything up the tree. When no slot has been published yet — the header
 * publishes it via a ref callback during commit, before paint — the children
 * render inline, which also keeps the actions assertable when a page is
 * rendered outside the shell (tests, Storybook).
 */
export function PageHeaderActions({ children }: { children: ReactNode }) {
  const { actionsSlot } = useStore();
  if (actionsSlot) return createPortal(children, actionsSlot);
  return <>{children}</>;
}
